export interface CollapseTransitionOptions {
  duration?: number;
  easing?: string;
  onComplete?: () => void;
}

export interface CollapseControllerOptions {
  open?: boolean;
  onChange?: (open: boolean) => void;
}

export interface CollapseController {
  readonly isControlled: boolean;
  readonly isOpen: boolean;
  sync(open?: boolean): void;
  setOpen(open: boolean, content?: HTMLElement, options?: CollapseTransitionOptions): void;
  open(content?: HTMLElement, options?: CollapseTransitionOptions): void;
  close(content?: HTMLElement, options?: CollapseTransitionOptions): void;
  toggle(content?: HTMLElement, options?: CollapseTransitionOptions): void;
  cancel(): void;
}

const DEFAULT_DURATION = 200;

function getDuration(content: HTMLElement, fallback: number): number {
  const view = content.ownerDocument.defaultView;
  const computedDuration = view?.getComputedStyle(content).transitionDuration;
  const rawDuration = computedDuration?.split(",")[0]?.trim();

  if (!rawDuration) {
    return fallback;
  }

  const parsedDuration = parseFloat(rawDuration);

  if (!Number.isFinite(parsedDuration)) {
    return fallback;
  }

  return rawDuration.endsWith("ms") ? parsedDuration : parsedDuration * 1000;
}

/**
 * Animates a content element between its collapsed and measured heights.
 * The returned function cancels the active transition and invokes no callback.
 */
export function transitionCollapse(
  content: HTMLElement,
  open: boolean,
  options: CollapseTransitionOptions = {},
): () => void {
  const duration = Math.max(0, options.duration ?? DEFAULT_DURATION);
  const easing = options.easing ?? "ease";
  const previousHeight = content.getBoundingClientRect().height;
  const targetHeight = open ? content.scrollHeight : 0;
  let finished = false;

  content.hidden = false;
  content.style.height = `${open ? previousHeight : content.scrollHeight}px`;
  content.style.overflow = "hidden";
  content.style.transition = "none";
  content.getBoundingClientRect();
  content.style.transition = `height ${duration}ms ${easing}`;
  content.style.height = `${targetHeight}px`;

  const finish = () => {
    if (finished) {
      return;
    }

    finished = true;
    content.removeEventListener("transitionend", handleTransitionEnd);
    window.clearTimeout(timer);
    content.style.height = "";
    content.style.overflow = "";
    content.style.transition = "";

    if (!open) {
      content.hidden = true;
    }

    options.onComplete?.();
  };

  const handleTransitionEnd = (event: Event) => {
    const transitionEvent = event as TransitionEvent;

    if (transitionEvent.target === content && transitionEvent.propertyName === "height") {
      finish();
    }
  };

  const timer = window.setTimeout(
    finish,
    Math.max(duration, getDuration(content, duration)) + 50,
  );

  content.addEventListener("transitionend", handleTransitionEnd);

  return () => {
    if (finished) {
      return;
    }

    finished = true;
    content.removeEventListener("transitionend", handleTransitionEnd);
    window.clearTimeout(timer);
    content.style.height = "";
    content.style.overflow = "";
    content.style.transition = "";
  };
}

class CollapseControllerImpl implements CollapseController {
  private controlledOpen: boolean | undefined;

  private internalOpen: boolean;

  private cancelTransition?: () => void;

  constructor(private readonly options: CollapseControllerOptions = {}) {
    this.controlledOpen = options.open;
    this.internalOpen = options.open ?? false;
  }

  get isControlled(): boolean {
    return this.controlledOpen !== undefined;
  }

  get isOpen(): boolean {
    return this.controlledOpen ?? this.internalOpen;
  }

  sync(open?: boolean): void {
    const previousOpen = this.isOpen;
    this.controlledOpen = open;

    if (open !== undefined) {
      this.internalOpen = open;
    }

    if (previousOpen !== this.isOpen) {
      this.options.onChange?.(this.isOpen);
    }
  }

  setOpen(
    open: boolean,
    content?: HTMLElement,
    options?: CollapseTransitionOptions,
  ): void {
    const previousOpen = this.isOpen;

    if (!this.isControlled) {
      this.internalOpen = open;
    }

    if (previousOpen !== open) {
      this.options.onChange?.(open);
    }

    this.cancelTransition?.();
    this.cancelTransition = content
      ? transitionCollapse(content, open, options)
      : undefined;
  }

  open(content?: HTMLElement, options?: CollapseTransitionOptions): void {
    this.setOpen(true, content, options);
  }

  close(content?: HTMLElement, options?: CollapseTransitionOptions): void {
    this.setOpen(false, content, options);
  }

  toggle(content?: HTMLElement, options?: CollapseTransitionOptions): void {
    this.setOpen(!this.isOpen, content, options);
  }

  cancel(): void {
    this.cancelTransition?.();
    this.cancelTransition = undefined;
  }
}

export function createCollapseController(
  options: CollapseControllerOptions = {},
): CollapseController {
  return new CollapseControllerImpl(options);
}
